import React from 'react';
import {
    Checkbox,
    IconButton,
    MessageBar,
    MessageBarType,
    SearchBox,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TooltipHost,
    useTheme,
} from '@fluentui/react';
import { IStorefrontAttributeSetting } from '../../models/Storefront';
import { useGetStorefrontAvailableAttributesQuery } from '../../store/endpoints/StorefrontSettingsEndpoints';

interface IStorefrontAttributesEditorProps {
    storefront: string;
    value?: IStorefrontAttributeSetting[];
    onChange: (next: IStorefrontAttributeSetting[]) => void;
}

// Re-numbers sortOrder to match array position so the persisted order is always dense and unambiguous.
const normalize = (list: IStorefrontAttributeSetting[]): IStorefrontAttributeSetting[] =>
    list.map((a, i) => ({ ...a, sortOrder: i }));

const StorefrontAttributesEditor: React.FC<IStorefrontAttributesEditorProps> = (props) => {
    const { storefront, value, onChange } = props;
    const theme = useTheme();
    const attrsQuery = useGetStorefrontAvailableAttributesQuery(storefront, { skip: !storefront });
    const [filter, setFilter] = React.useState('');

    const limit = attrsQuery.data?.attributeLimit ?? 20;
    const available = attrsQuery.data?.attributes ?? [];

    // Sample values keyed by attribute, for the muted hint next to each attribute name.
    const valuesByKey = React.useMemo(() => {
        const map: Record<string, string[]> = {};
        available.forEach((a) => { map[a.key] = a.values ?? []; });
        return map;
    }, [available]);

    const selected = React.useMemo(
        () => [...(value ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
        [value]
    );
    const selectedKeys = React.useMemo(() => new Set(selected.map((s) => s.key)), [selected]);
    const atLimit = selected.length >= limit;

    const addable = available
        .filter((a) => !selectedKeys.has(a.key))
        .filter((a) => a.key.toLowerCase().includes(filter.trim().toLowerCase()));

    const addAttribute = (key: string) => {
        if (atLimit || selectedKeys.has(key)) return;
        // Sensible default: a newly picked attribute is shown, searchable, and comparable.
        onChange(normalize([...selected, { key, visible: true, searchable: true, comparable: true }]));
    };

    const removeAttribute = (key: string) => {
        onChange(normalize(selected.filter((s) => s.key !== key)));
    };

    const setFlag = (key: string, flag: 'visible' | 'searchable' | 'comparable', checked: boolean) => {
        onChange(normalize(selected.map((s) => (s.key === key ? { ...s, [flag]: checked } : s))));
    };

    const move = (index: number, dir: -1 | 1) => {
        const target = index + dir;
        if (target < 0 || target >= selected.length) return;
        const next = [...selected];
        [next[index], next[target]] = [next[target], next[index]];
        onChange(normalize(next));
    };

    if (attrsQuery.isLoading) {
        return <Spinner size={SpinnerSize.medium} label="Loading product attributes…" />;
    }

    const rowBorder = `1px solid ${theme.palette.neutralLight}`;

    const sampleLine = (samples: string[]) =>
        samples.length > 0 ? (
            <Text variant="small" block nowrap styles={{ root: { color: theme.palette.neutralSecondary } }}>
                {samples.slice(0, 4).join(', ')}{samples.length > 4 ? '…' : ''}
            </Text>
        ) : null;

    // Left/top column: the attributes available to add.
    const availableColumn = (
        <Stack tokens={{ childrenGap: '0.5em' }}>
            <h3 style={{ marginBottom: 0 }}>Available attributes</h3>
            {atLimit && (
                <MessageBar messageBarType={MessageBarType.warning}>
                    Attribute limit reached ({limit}). Remove one to add another.
                </MessageBar>
            )}
            <SearchBox
                placeholder="Filter attributes"
                value={filter}
                onChange={(_e, v) => setFilter(v ?? '')}
            />
            {available.length === 0 ? (
                <Text styles={{ root: { color: theme.palette.neutralSecondary } }}>
                    No product attributes were found for this storefront&apos;s products.
                </Text>
            ) : addable.length === 0 ? (
                <Text variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
                    {filter ? 'No matching attributes.' : 'All available attributes have been added.'}
                </Text>
            ) : (
                <Stack tokens={{ childrenGap: '0.25em' }} styles={{ root: { maxHeight: 420, overflowY: 'auto' } }}>
                    {addable.map((attr) => (
                        <Stack
                            key={attr.key}
                            horizontal
                            verticalAlign="center"
                            tokens={{ childrenGap: '0.5em' }}
                            styles={{ root: { padding: '0.25em 0.5em', borderRadius: 4 } }}
                        >
                            <IconButton
                                iconProps={{ iconName: 'Add' }}
                                ariaLabel={`Add ${attr.key}`}
                                disabled={atLimit}
                                onClick={() => addAttribute(attr.key)}
                            />
                            <Stack.Item grow styles={{ root: { minWidth: 0 } }}>
                                <Text styles={{ root: { fontWeight: 600 } }}>{attr.key}</Text>
                                {sampleLine(attr.values ?? [])}
                            </Stack.Item>
                        </Stack>
                    ))}
                </Stack>
            )}
        </Stack>
    );

    // Right/bottom column: the selected attributes, ordered, with per-capability toggles.
    const selectedColumn = (
        <Stack tokens={{ childrenGap: '0.5em' }}>
            <h3 style={{ marginBottom: 0 }}>Selected attributes</h3>
            {selected.length === 0 ? (
                <Text styles={{ root: { color: theme.palette.neutralSecondary } }}>
                    No attributes selected yet. Add some from the list of available attributes.
                </Text>
            ) : (
                <Stack tokens={{ childrenGap: '0.5em' }}>
                    {selected.map((attr, index) => (
                        <Stack
                            key={attr.key}
                            tokens={{ childrenGap: '0.5em' }}
                            styles={{ root: { border: rowBorder, borderRadius: 4, padding: '0.5em 0.75em' } }}
                        >
                            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: '0.5em' }}>
                                <Stack.Item>
                                    <Stack>
                                        <IconButton
                                            iconProps={{ iconName: 'ChevronUpSmall' }}
                                            ariaLabel="Move up"
                                            disabled={index === 0}
                                            onClick={() => move(index, -1)}
                                            styles={{ root: { height: 16 } }}
                                        />
                                        <IconButton
                                            iconProps={{ iconName: 'ChevronDownSmall' }}
                                            ariaLabel="Move down"
                                            disabled={index === selected.length - 1}
                                            onClick={() => move(index, 1)}
                                            styles={{ root: { height: 16 } }}
                                        />
                                    </Stack>
                                </Stack.Item>
                                <Stack.Item grow styles={{ root: { minWidth: 0 } }}>
                                    <Text styles={{ root: { fontWeight: 600 } }}>{attr.key}</Text>
                                    {sampleLine(valuesByKey[attr.key] ?? [])}
                                </Stack.Item>
                                <IconButton
                                    iconProps={{ iconName: 'Delete' }}
                                    ariaLabel={`Remove ${attr.key}`}
                                    onClick={() => removeAttribute(attr.key)}
                                />
                            </Stack>
                            <Stack horizontal wrap tokens={{ childrenGap: '0.75em' }} verticalAlign="center">
                                <TooltipHost content="Show this attribute's value on the product.">
                                    <Checkbox
                                        label="Visible"
                                        checked={attr.visible ?? false}
                                        onChange={(_e, c) => setFlag(attr.key, 'visible', c ?? false)}
                                    />
                                </TooltipHost>
                                <TooltipHost content="Let shoppers filter/search products by this attribute.">
                                    <Checkbox
                                        label="Searchable"
                                        checked={attr.searchable ?? false}
                                        onChange={(_e, c) => setFlag(attr.key, 'searchable', c ?? false)}
                                    />
                                </TooltipHost>
                                <TooltipHost content="Include this attribute when comparing products.">
                                    <Checkbox
                                        label="Comparable"
                                        checked={attr.comparable ?? false}
                                        onChange={(_e, c) => setFlag(attr.key, 'comparable', c ?? false)}
                                    />
                                </TooltipHost>
                            </Stack>
                        </Stack>
                    ))}
                </Stack>
            )}
        </Stack>
    );

    return (
        <Stack tokens={{ childrenGap: '1em' }}>
            <Stack tokens={{ childrenGap: '0.25em' }}>
                <h2 style={{ marginBottom: 0 }}>Searchable &amp; Comparable Attributes</h2>
                <Text variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
                    Choose which product attributes shoppers can see, filter/search, and compare on this
                    storefront. Attributes come from your storefront&apos;s products.
                </Text>
                <Text variant="small" styles={{ root: { color: atLimit ? theme.palette.redDark : theme.palette.neutralSecondary } }}>
                    {selected.length} / {limit} selected
                </Text>
            </Stack>

            {attrsQuery.isError && (
                <MessageBar messageBarType={MessageBarType.error}>
                    Couldn&apos;t load available attributes for this storefront.
                </MessageBar>
            )}

            {/* Two columns on wide screens; wraps to Available-then-Selected when narrow. */}
            <Stack horizontal wrap tokens={{ childrenGap: '1.5em' }} styles={{ root: { alignItems: 'flex-start' } }}>
                <Stack.Item grow styles={{ root: { flexBasis: 320, minWidth: 280, maxWidth: 520 } }}>
                    {availableColumn}
                </Stack.Item>
                <Stack.Item grow styles={{ root: { flexBasis: 380, minWidth: 300 } }}>
                    {selectedColumn}
                </Stack.Item>
            </Stack>
        </Stack>
    );
};

export default StorefrontAttributesEditor;
